import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    App,
    Button,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd'
import type { TablePaginationConfig } from 'antd'
import { EditOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import {
    ReadonlyDatePicker,
    ReadonlyRangePicker,
} from '../../components/common/DatePickerControls'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../components/common/FilterBar'
import {
    PaginatedListCard,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../../components/common/PaginatedListTable'
import { PageLayout } from '../../components/layout'
import { usePermissions } from '../../hooks/usePermissions'
import { useDedupedMessage } from '../../hooks/useDedupedMessage'
import {
    getPackageManagementOptions,
    getPackageProjectDevices,
    getPackageProjects,
    updatePackageDevice,
} from '../../services/packageManagementService'
import { localeService } from '../../services/localeService'
import { getPricingSettings } from '../../services/systemService'
import type {
    PackageDeviceRow,
    PackageDeviceStatus,
    PackageDeviceSummary,
    PackageProjectOption,
    PackageProjectRow,
    PackageProjectStatus,
    PackageRegionOption,
    PackageRenewalStatus,
} from '../../types/packageManagement'
import type { VersionOption } from '../../types/version'
import { toFullDayIsoRange } from '../../utils/packageDateRange'
import {
    formatPackageBusinessDate,
    formatPackageFormDate,
    isSamePackageBusinessDate,
    PACKAGE_BUSINESS_DATE_FORMAT,
    parsePackageBusinessDate,
} from '../../utils/packageBusinessDate'
import {
    getDevicePackageDisplayName,
    getDevicePackageOptionDisplayName,
} from '../../utils/devicePackageDisplay'
import { filterRegionSelectOption } from '../../utils/regionSearch'
import {
    filterSearchOptionByLabel,
    normalizeSearchText,
} from '../../utils/searchText'

interface PackageDeviceEditFormValues {
    deviceVersion?: string | null
    lureExpireDate?: Dayjs | null
    platformExpireDate?: Dayjs | null
}

interface RequiredPackageDeviceEditFormValues {
    deviceVersion: string
    lureExpireDate: Dayjs
    platformExpireDate: Dayjs
}

const DATE_FORMAT = PACKAGE_BUSINESS_DATE_FORMAT
const PACKAGE_PROJECT_TABLE_SCROLL_X = 1150

interface PackageProjectFilters {
    selectedProjectId?: number
    selectedRegionId?: number
    statusFilter: 'all' | PackageProjectStatus
}

interface PackageDeviceFilters {
    deviceNameKeyword: string
    deviceVersionFilter: 'all' | string
    deviceStatusFilter: 'all' | PackageDeviceStatus
    lureDateRange: [string, string] | null
}

const DEFAULT_PROJECT_FILTERS: PackageProjectFilters = {
    selectedProjectId: undefined,
    selectedRegionId: undefined,
    statusFilter: 'all',
}

const DEFAULT_DEVICE_FILTERS: PackageDeviceFilters = {
    deviceNameKeyword: '',
    deviceVersionFilter: 'all',
    deviceStatusFilter: 'all',
    lureDateRange: null,
}

const STATUS_TAG_COLOR: Record<PackageProjectStatus, string> = {
    normal: 'success',
    partial_expired: 'warning',
    all_expired: 'error',
}

const DEVICE_STATUS_TAG_COLOR: Record<PackageDeviceStatus, string> = {
    online: 'success',
    offline: 'default',
}

const RENEWAL_STATUS_TAG_COLOR: Record<PackageRenewalStatus, string> = {
    renewed: 'success',
    pending: 'warning',
}

const EMPTY_DEVICE_SUMMARY: PackageDeviceSummary = {
    total: 0,
    online: 0,
    offline: 0,
    renewed: 0,
    pending: 0,
}
const DEFAULT_LURE_VALIDITY_DAYS = 30

const actionLinkButtonStyle: React.CSSProperties = {
    paddingInline: 4,
    height: 24,
    lineHeight: '24px',
    whiteSpace: 'normal',
}

const formatEventDateTime = (value: string | null): string =>
    localeService.formatDateTime(value)

const requireDeviceEditValues = (
    values: PackageDeviceEditFormValues
): RequiredPackageDeviceEditFormValues => ({
    deviceVersion: values.deviceVersion ?? '',
    lureExpireDate: values.lureExpireDate as Dayjs,
    platformExpireDate: values.platformExpireDate as Dayjs,
})

const PackageManagementV1: React.FC = () => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t } = useTranslation(['admin', 'navigation', 'device'])
    const { canAccessPackageManagement, canReadPackageDevices } =
        usePermissions()
    const [selectedProjectId, setSelectedProjectId] = useState<
        number | undefined
    >(undefined)
    const [selectedRegionId, setSelectedRegionId] = useState<
        number | undefined
    >(undefined)
    const [statusFilter, setStatusFilter] = useState<
        'all' | PackageProjectStatus
    >('all')
    const [appliedProjectFilters, setAppliedProjectFilters] =
        useState<PackageProjectFilters>({ ...DEFAULT_PROJECT_FILTERS })
    const [projectPage, setProjectPage] = useState(1)
    const [projectPageSize, setProjectPageSize] = useState(10)
    const [projectQueryTrigger, setProjectQueryTrigger] = useState(0)
    const [projectTotal, setProjectTotal] = useState(0)
    const [projects, setProjects] = useState<PackageProjectRow[]>([])
    const [projectLoading, setProjectLoading] = useState(false)

    const [deviceModalProject, setDeviceModalProject] =
        useState<PackageProjectRow | null>(null)
    const [deviceNameKeyword, setDeviceNameKeyword] = useState('')
    const [deviceVersionFilter, setDeviceVersionFilter] = useState<
        'all' | string
    >('all')
    const [deviceStatusFilter, setDeviceStatusFilter] = useState<
        'all' | PackageDeviceStatus
    >('all')
    const [lureDateRange, setLureDateRange] = useState<[string, string] | null>(
        null
    )
    const [appliedDeviceFilters, setAppliedDeviceFilters] =
        useState<PackageDeviceFilters>({ ...DEFAULT_DEVICE_FILTERS })
    const [devicePage, setDevicePage] = useState(1)
    const [devicePageSize, setDevicePageSize] = useState(10)
    const [deviceQueryTrigger, setDeviceQueryTrigger] = useState(0)
    const [deviceTotal, setDeviceTotal] = useState(0)
    const [devices, setDevices] = useState<PackageDeviceRow[]>([])
    const [deviceSummary, setDeviceSummary] =
        useState<PackageDeviceSummary>(EMPTY_DEVICE_SUMMARY)
    const [deviceLoading, setDeviceLoading] = useState(false)

    const [deviceVersionOptions, setDeviceVersionOptions] = useState<
        VersionOption[]
    >([])
    const [editableDeviceVersionOptions, setEditableDeviceVersionOptions] =
        useState<VersionOption[]>([])
    const [projectOptions, setProjectOptions] = useState<
        PackageProjectOption[]
    >([])
    const [regionOptions, setRegionOptions] = useState<PackageRegionOption[]>(
        []
    )
    const [editingDevice, setEditingDevice] = useState<PackageDeviceRow | null>(
        null
    )
    const [savingDevice, setSavingDevice] = useState(false)
    const [editDeviceForm] = Form.useForm<PackageDeviceEditFormValues>()
    const [lureValidityDays, setLureValidityDays] = useState(
        DEFAULT_LURE_VALIDITY_DAYS
    )

    const loadOptions = useCallback(async () => {
        try {
            const options = await getPackageManagementOptions()
            const deviceVersions = Array.isArray(options.deviceVersions)
                ? options.deviceVersions
                : []
            const normalizedDeviceVersionOptions = Array.isArray(
                options.deviceVersionOptions
            )
                ? options.deviceVersionOptions
                : deviceVersions.map((version) => ({
                      key: null,
                      name: version,
                      value: version,
                  }))
            setDeviceVersionOptions(normalizedDeviceVersionOptions)
            setEditableDeviceVersionOptions(
                Array.isArray(options.editableDeviceVersionOptions)
                    ? options.editableDeviceVersionOptions
                    : (Array.isArray(options.editableDeviceVersions)
                          ? options.editableDeviceVersions
                          : deviceVersions
                      ).map((version) => ({
                          key: null,
                          name: version,
                          value: version,
                      }))
            )
            setProjectOptions(options.projectOptions)
            setRegionOptions(options.regionOptions)
        } catch {
            setDeviceVersionOptions([])
            setEditableDeviceVersionOptions([])
            setProjectOptions([])
            setRegionOptions([])
        }
    }, [])

    const loadProjects = useCallback(async () => {
        void projectQueryTrigger

        setProjectLoading(true)
        try {
            const response = await getPackageProjects({
                page: projectPage,
                pageSize: projectPageSize,
                projectId: appliedProjectFilters.selectedProjectId,
                regionId: appliedProjectFilters.selectedRegionId,
                statusFilter: appliedProjectFilters.statusFilter,
            })
            setProjects(response.items)
            setProjectTotal(response.total)
        } catch {
            message.error(
                t('admin:package.messages.loadProjectsFailed', {
                    defaultValue: 'Load project list failed',
                })
            )
        } finally {
            setProjectLoading(false)
        }
    }, [
        message,
        appliedProjectFilters,
        projectPage,
        projectPageSize,
        projectQueryTrigger,
        t,
    ])

    const loadDevices = useCallback(async () => {
        void deviceQueryTrigger

        if (!deviceModalProject || !canReadPackageDevices) {
            return
        }
        setDeviceLoading(true)
        try {
            const response = await getPackageProjectDevices(
                deviceModalProject.id,
                {
                    page: devicePage,
                    pageSize: devicePageSize,
                    deviceName:
                        appliedDeviceFilters.deviceNameKeyword || undefined,
                    deviceVersion: appliedDeviceFilters.deviceVersionFilter,
                    deviceStatus: appliedDeviceFilters.deviceStatusFilter,
                    lureStart: appliedDeviceFilters.lureDateRange?.[0],
                    lureEnd: appliedDeviceFilters.lureDateRange?.[1],
                }
            )
            setDevices(response.items)
            setDeviceTotal(response.total)
            setDeviceSummary(response.summary)
        } catch {
            message.error(
                t('admin:package.messages.loadDevicesFailed', {
                    defaultValue: 'Load device list failed',
                })
            )
        } finally {
            setDeviceLoading(false)
        }
    }, [
        deviceModalProject,
        appliedDeviceFilters,
        devicePage,
        devicePageSize,
        deviceQueryTrigger,
        message,
        t,
        canReadPackageDevices,
    ])

    useEffect(() => {
        if (canAccessPackageManagement) {
            void loadOptions()
        }
    }, [canAccessPackageManagement, loadOptions])

    useEffect(() => {
        if (canAccessPackageManagement) {
            void loadProjects()
        }
    }, [canAccessPackageManagement, loadProjects])

    useEffect(() => {
        if (!canAccessPackageManagement) {
            return
        }

        const loadSettings = async () => {
            try {
                const settings = await getPricingSettings()
                setLureValidityDays(
                    settings.reminders.lureValidityDays ||
                        DEFAULT_LURE_VALIDITY_DAYS
                )
            } catch {
                setLureValidityDays(DEFAULT_LURE_VALIDITY_DAYS)
            }
        }

        void loadSettings()
    }, [canAccessPackageManagement])

    useEffect(() => {
        void loadDevices()
    }, [loadDevices])

    const applyProjectFilters = () => {
        setAppliedProjectFilters({
            selectedProjectId,
            selectedRegionId,
            statusFilter,
        })
        setProjectPage(1)
        setProjectQueryTrigger((value) => value + 1)
    }

    const resetFilters = () => {
        setSelectedProjectId(undefined)
        setSelectedRegionId(undefined)
        setStatusFilter('all')
        setAppliedProjectFilters({ ...DEFAULT_PROJECT_FILTERS })
        setProjectPage(1)
    }

    const applyDeviceFilters = () => {
        const nextDeviceNameKeyword = normalizeSearchText(deviceNameKeyword)
        setAppliedDeviceFilters({
            deviceNameKeyword: nextDeviceNameKeyword,
            deviceVersionFilter,
            deviceStatusFilter,
            lureDateRange: lureDateRange ? [...lureDateRange] : null,
        })
        setDevicePage(1)
        setDeviceQueryTrigger((value) => value + 1)
    }

    const resetDeviceFilters = () => {
        setDeviceNameKeyword('')
        setDeviceVersionFilter('all')
        setDeviceStatusFilter('all')
        setLureDateRange(null)
        setAppliedDeviceFilters({ ...DEFAULT_DEVICE_FILTERS })
        setDevicePage(1)
    }

    const closeDeviceModal = () => {
        setDeviceModalProject(null)
        setDevices([])
        setDeviceTotal(0)
        setDeviceSummary(EMPTY_DEVICE_SUMMARY)
        closeEditDeviceModal()
        resetDeviceFilters()
    }

    const handleViewDevices = (project: PackageProjectRow) => {
        if (!canReadPackageDevices) {
            return
        }
        setDeviceModalProject(project)
        resetDeviceFilters()
    }

    const openEditDeviceModal = (device: PackageDeviceRow) => {
        if (!canReadPackageDevices) {
            return
        }
        setEditingDevice(device)
        editDeviceForm.setFieldsValue({
            deviceVersion: device.deviceVersion,
            lureExpireDate: parsePackageBusinessDate(device.lureExpireDate),
            platformExpireDate: parsePackageBusinessDate(
                device.platformExpireDate
            ),
        })
    }

    const closeEditDeviceModal = () => {
        setEditingDevice(null)
        editDeviceForm.resetFields()
    }

    const saveDeviceEdit = async () => {
        if (!editingDevice || !canReadPackageDevices) {
            return
        }
        setSavingDevice(true)
        try {
            const values = requireDeviceEditValues(
                await editDeviceForm.validateFields()
            )
            const payload: Parameters<typeof updatePackageDevice>[1] = {}
            const nextDeviceVersion = values.deviceVersion
            if (nextDeviceVersion !== editingDevice.deviceVersion) {
                payload.deviceVersion = nextDeviceVersion
            }
            if (
                !isSamePackageBusinessDate(
                    editingDevice.lureExpireDate,
                    values.lureExpireDate
                )
            ) {
                payload.lureExpireDate = values.lureExpireDate.toISOString()
            }
            if (
                !isSamePackageBusinessDate(
                    editingDevice.platformExpireDate,
                    values.platformExpireDate
                )
            ) {
                payload.platformExpireDate =
                    values.platformExpireDate.toISOString()
            }

            if (Object.keys(payload).length === 0) {
                closeEditDeviceModal()
                return
            }

            await updatePackageDevice(editingDevice.id, payload)
            closeEditDeviceModal()
            await Promise.all([loadDevices(), loadProjects(), loadOptions()])
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) {
                return
            }
            message.error(
                t('admin:package.messages.saveDeviceFailed', {
                    defaultValue: 'Save device failed',
                })
            )
        } finally {
            setSavingDevice(false)
        }
    }

    const renewLureExpireDate = () => {
        editDeviceForm.setFieldValue(
            'lureExpireDate',
            dayjs().add(lureValidityDays, 'day')
        )
    }

    const projectPagination = useMemo<TablePaginationConfig>(
        () =>
            buildPaginatedListPagination({
                current: projectPage,
                pageSize: projectPageSize,
                total: projectTotal,
                showTotal: (total) =>
                    t('admin:package.pagination.totalProjects', {
                        total,
                        defaultValue: 'Total {{total}} projects',
                    }),
                onChange: (page, pageSize) => {
                    setProjectPage(page)
                    setProjectPageSize(pageSize)
                },
            }),
        [projectPage, projectPageSize, projectTotal, t]
    )

    const devicePagination = useMemo<TablePaginationConfig>(
        () => ({
            current: devicePage,
            pageSize: devicePageSize,
            total: deviceTotal,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) =>
                t('admin:package.deviceModal.pagination.totalDevices', {
                    total,
                    defaultValue: 'Total {{total}} devices',
                }),
            size: 'small',
            onChange: (page, pageSize) => {
                setDevicePage(page)
                setDevicePageSize(pageSize)
            },
        }),
        [devicePage, devicePageSize, deviceTotal, t]
    )
    const projectSelectOptions = useMemo(
        () =>
            projectOptions.map((project) => ({
                label: project.name,
                value: project.id,
            })),
        [projectOptions]
    )
    const regionSelectOptions = useMemo(
        () =>
            regionOptions.map((region) => ({
                label: region.label,
                value: region.id,
            })),
        [regionOptions]
    )

    if (!canAccessPackageManagement) {
        return null
    }

    return (
        <PageLayout
            title={t('admin:package.title', { defaultValue: 'Device Version' })}
            breadcrumb={[
                {
                    title: t('navigation:breadcrumb.projectManagement', {
                        defaultValue: 'Project Management',
                    }),
                },
                {
                    title: t('navigation:breadcrumb.packageManagementV1', {
                        defaultValue: 'Device Version',
                    }),
                },
            ]}
        >
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <FilterBar>
                    <FilterField
                        label={t('admin:package.filters.projectNameLabel', {
                            defaultValue: 'Project Name',
                        })}
                    >
                        <Select<number>
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder={t(
                                'admin:package.filters.projectNamePlaceholder',
                                {
                                    defaultValue:
                                        'Select or search project name',
                                }
                            )}
                            value={selectedProjectId}
                            onChange={setSelectedProjectId}
                            options={projectSelectOptions}
                            filterOption={filterSearchOptionByLabel}
                        />
                    </FilterField>
                    <FilterField
                        label={t('admin:package.filters.regionLabel', {
                            defaultValue: 'Region',
                        })}
                    >
                        <Select<number>
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder={t(
                                'admin:package.filters.regionPlaceholder',
                                {
                                    defaultValue: 'Select or search region',
                                }
                            )}
                            value={selectedRegionId}
                            onChange={setSelectedRegionId}
                            options={regionSelectOptions}
                            filterOption={filterRegionSelectOption}
                        />
                    </FilterField>
                    <FilterField
                        label={t('admin:package.filters.statusLabel', {
                            defaultValue: 'Current Status',
                        })}
                    >
                        <Select<'all' | PackageProjectStatus>
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                {
                                    label: t(
                                        'admin:package.filters.allStatus',
                                        {
                                            defaultValue: 'All Statuses',
                                        }
                                    ),
                                    value: 'all',
                                },
                                ...(
                                    [
                                        'normal',
                                        'partial_expired',
                                        'all_expired',
                                    ] as PackageProjectStatus[]
                                ).map((status) => ({
                                    label: t(
                                        `admin:package.status.${status}`
                                    ),
                                    value: status,
                                })),
                            ]}
                        />
                    </FilterField>
                    <FilterActions>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={applyProjectFilters}
                            loading={projectLoading}
                        >
                            {t('common:buttons.search')}
                        </Button>
                        <Button onClick={resetFilters}>
                            {t('admin:package.actions.reset', {
                                defaultValue: 'Reset',
                            })}
                        </Button>
                    </FilterActions>
                </FilterBar>

                <PaginatedListCard>
                    <Table<PackageProjectRow>
                        rowKey="id"
                        dataSource={projects}
                        loading={projectLoading}
                        size={PAGINATED_LIST_TABLE_SIZE}
                        tableLayout="fixed"
                        scroll={{ x: PACKAGE_PROJECT_TABLE_SCROLL_X }}
                        pagination={projectPagination}
                        columns={[
                        {
                            title: t('admin:package.columns.projectName', {
                                defaultValue: 'Project Name',
                            }),
                            dataIndex: 'projectName',
                            width: 260,
                            ellipsis: true,
                            render: (value: string) => (
                                <strong>{renderPaginatedListText(value)}</strong>
                            ),
                        },
                        {
                            title: t('admin:package.columns.region', {
                                defaultValue: 'Region',
                            }),
                            dataIndex: 'region',
                            width: 240,
                            ellipsis: true,
                            render: renderPaginatedListText,
                        },
                        {
                            title: t('admin:package.columns.status', {
                                defaultValue: 'Current Status',
                            }),
                            dataIndex: 'status',
                            width: 160,
                            render: (status: PackageProjectStatus) => (
                                <Tag color={STATUS_TAG_COLOR[status]}>
                                    {t(`admin:package.status.${status}`)}
                                </Tag>
                            ),
                        },
                        {
                            title: t('admin:package.columns.deviceTotal', {
                                defaultValue: 'Device Total',
                            }),
                            dataIndex: 'deviceTotal',
                            width: 160,
                        },
                        {
                            title: t(
                                'admin:package.columns.renewedDeviceTotal',
                                {
                                    defaultValue: 'Renewed Devices',
                                }
                            ),
                            dataIndex: 'renewedDeviceTotal',
                            width: 180,
                        },
                        {
                            title: t('admin:package.columns.actions', {
                                defaultValue: 'Actions',
                            }),
                            key: 'actions',
                            width: 150,
                            fixed: 'right' as const,
                            render: (_, project) =>
                                canReadPackageDevices ? (
                                    <Button
                                        type="link"
                                        icon={<EyeOutlined aria-hidden />}
                                        onClick={() =>
                                            handleViewDevices(project)
                                        }
                                        style={actionLinkButtonStyle}
                                    >
                                        {t(
                                            'admin:package.actions.viewDevices',
                                            {
                                                defaultValue:
                                                    'View Devices',
                                            }
                                        )}
                                    </Button>
                                ) : null,
                        },
                        ]}
                    />
                </PaginatedListCard>
            </Space>
            <Modal
                open={Boolean(deviceModalProject)}
                title={t('admin:package.deviceModal.title', {
                    projectName: deviceModalProject?.projectName || '',
                    defaultValue: '{{projectName}} Device Details',
                })}
                footer={null}
                width="72vw"
                maskClosable={false}
                destroyOnClose
                onCancel={closeDeviceModal}
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Typography.Text type="secondary">
                        {t('admin:package.deviceModal.description', {
                            defaultValue:
                                'Filter quickly by device name, device version, device status, and lure expiration date, and edit device version, platform fee expiration date, or set lure expiration date with one click.',
                        })}
                    </Typography.Text>

                    <Row gutter={[24, 12]}>
                        {[
                            ['total', deviceSummary.total],
                            ['online', deviceSummary.online],
                            ['offline', deviceSummary.offline],
                            ['renewed', deviceSummary.renewed],
                            ['pending', deviceSummary.pending],
                        ].map(([key, value]) => (
                            <Col xs={12} md={4} key={key}>
                                <Typography.Text type="secondary">
                                    {t(
                                        `admin:package.deviceModal.summary.${key}`
                                    )}
                                </Typography.Text>
                                <Typography.Title
                                    level={3}
                                    style={{ margin: '8px 0 0' }}
                                >
                                    {value}
                                </Typography.Title>
                            </Col>
                        ))}
                    </Row>

                    <FilterBar>
                        <FilterField
                            label={t(
                                'admin:package.deviceModal.filters.deviceNameLabel',
                                {
                                    defaultValue: 'Device Name',
                                }
                            )}
                        >
                            <Input
                                allowClear
                                placeholder={t(
                                    'admin:package.deviceModal.filters.deviceNamePlaceholder',
                                    { defaultValue: 'Search device name' }
                                )}
                                value={deviceNameKeyword}
                                onChange={(event) =>
                                    setDeviceNameKeyword(event.target.value)
                                }
                                onPressEnter={applyDeviceFilters}
                            />
                        </FilterField>
                        <FilterField
                            label={t(
                                'admin:package.deviceModal.filters.versionLabel',
                                {
                                    defaultValue: 'Device Version',
                                }
                            )}
                        >
                            <Select<'all' | string>
                                value={deviceVersionFilter}
                                onChange={setDeviceVersionFilter}
                                options={[
                                    {
                                        label: t(
                                            'admin:package.deviceModal.filters.allVersions',
                                            {
                                                defaultValue: 'All Versions',
                                            }
                                        ),
                                        value: 'all',
                                    },
                                    ...deviceVersionOptions.map((option) => ({
                                        label: getDevicePackageOptionDisplayName(
                                            option,
                                            t
                                        ),
                                        value: option.value,
                                    })),
                                ]}
                            />
                        </FilterField>
                        <FilterField
                            label={t(
                                'admin:package.deviceModal.filters.deviceStatusLabel',
                                {
                                    defaultValue: 'Device Status',
                                }
                            )}
                        >
                            <Select<'all' | PackageDeviceStatus>
                                value={deviceStatusFilter}
                                onChange={setDeviceStatusFilter}
                                options={[
                                    {
                                        label: t(
                                            'admin:package.deviceModal.filters.allDeviceStatus',
                                            {
                                                defaultValue:
                                                    'All Device Statuses',
                                            }
                                        ),
                                        value: 'all',
                                    },
                                    ...(
                                        [
                                            'online',
                                            'offline',
                                        ] as PackageDeviceStatus[]
                                    ).map((status) => ({
                                        label: t(
                                            `admin:package.deviceModal.deviceStatus.${status}`
                                        ),
                                        value: status,
                                    })),
                                ]}
                            />
                        </FilterField>
                        <FilterField
                            label={t(
                                'admin:package.deviceModal.filters.lureDateRangeLabel',
                                {
                                    defaultValue: 'Lure Expiration Range',
                                }
                            )}
                        >
                            <ReadonlyRangePicker
                                format={DATE_FORMAT}
                                value={
                                    lureDateRange
                                        ? [
                                              dayjs(lureDateRange[0]),
                                              dayjs(lureDateRange[1]),
                                          ]
                                        : null
                                }
                                onChange={(values) =>
                                    setLureDateRange(toFullDayIsoRange(values))
                                }
                            />
                        </FilterField>
                        <FilterActions>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={applyDeviceFilters}
                                loading={deviceLoading}
                            >
                                {t('common:buttons.search')}
                            </Button>
                            <Button onClick={resetDeviceFilters}>
                                {t('admin:package.actions.reset', {
                                    defaultValue: 'Reset',
                                })}
                            </Button>
                        </FilterActions>
                    </FilterBar>

                    <Table<PackageDeviceRow>
                        rowKey="id"
                        dataSource={devices}
                        loading={deviceLoading}
                        size="small"
                        scroll={{ x: 1200 }}
                        pagination={devicePagination}
                        columns={[
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.deviceName',
                                    {
                                        defaultValue: 'Device Name',
                                    }
                                ),
                                dataIndex: 'deviceName',
                                width: 200,
                                fixed: 'left',
                                render: (value: string) => (
                                    <strong>{value}</strong>
                                ),
                            },
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.deviceVersion',
                                    {
                                        defaultValue: 'Device Version',
                                    }
                                ),
                                dataIndex: 'deviceVersion',
                                width: 150,
                                render: (
                                    value: string | null,
                                    device: PackageDeviceRow
                                ) =>
                                    value ? (
                                        <Tag color="blue">
                                            {getDevicePackageDisplayName(
                                                device.deviceVersionKey,
                                                value,
                                                t
                                            )}
                                        </Tag>
                                    ) : (
                                        '-'
                                    ),
                            },
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.lureExpireDate',
                                    {
                                        defaultValue: 'Lure Expiration Date',
                                    }
                                ),
                                dataIndex: 'lureExpireDate',
                                width: 160,
                                render: formatPackageBusinessDate,
                            },
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.platformExpireDate',
                                    {
                                        defaultValue:
                                            'Platform Fee Expiration Date',
                                    }
                                ),
                                dataIndex: 'platformExpireDate',
                                width: 170,
                                render: formatPackageBusinessDate,
                            },
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.deviceStatus',
                                    {
                                        defaultValue: 'Device Status',
                                    }
                                ),
                                dataIndex: 'deviceStatus',
                                width: 120,
                                render: (status: PackageDeviceStatus) => (
                                    <Tag
                                        color={DEVICE_STATUS_TAG_COLOR[status]}
                                    >
                                        {t(
                                            `admin:package.deviceModal.deviceStatus.${status}`
                                        )}
                                    </Tag>
                                ),
                            },
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.renewalStatus',
                                    {
                                        defaultValue: 'Renewal Status',
                                    }
                                ),
                                dataIndex: 'renewalStatus',
                                width: 120,
                                render: (status: PackageRenewalStatus) => (
                                    <Tag
                                        color={RENEWAL_STATUS_TAG_COLOR[status]}
                                    >
                                        {t(
                                            `admin:package.deviceModal.renewalStatus.${status}`
                                        )}
                                    </Tag>
                                ),
                            },
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.lastOnlineAt',
                                    {
                                        defaultValue: 'Last Online At',
                                    }
                                ),
                                dataIndex: 'lastOnlineAt',
                                width: 170,
                                render: formatEventDateTime,
                            },
                            {
                                title: t(
                                    'admin:package.deviceModal.columns.actions',
                                    {
                                        defaultValue: 'Actions',
                                    }
                                ),
                                key: 'actions',
                                width: 110,
                                fixed: 'right',
                                render: (_, device) => (
                                    <Button
                                        type="link"
                                        icon={<EditOutlined aria-hidden />}
                                        onClick={() =>
                                            openEditDeviceModal(device)
                                        }
                                        style={actionLinkButtonStyle}
                                    >
                                        {t(
                                            'admin:package.deviceModal.actions.edit',
                                            {
                                                defaultValue: 'Edit',
                                            }
                                        )}
                                    </Button>
                                ),
                            },
                        ]}
                    />
                </Space>
            </Modal>
            <Modal
                open={Boolean(editingDevice)}
                title={t('admin:package.editDeviceModal.title', {
                    defaultValue: 'Edit Device Information',
                })}
                okText={t('admin:package.editDeviceModal.actions.save', {
                    defaultValue: 'Save',
                })}
                cancelText={t('admin:package.editDeviceModal.actions.cancel', {
                    defaultValue: 'Cancel',
                })}
                width={468}
                maskClosable={false}
                destroyOnClose
                confirmLoading={savingDevice}
                onOk={() => {
                    void saveDeviceEdit()
                }}
                onCancel={closeEditDeviceModal}
            >
                <Typography.Paragraph type="secondary">
                    {t('admin:package.editDeviceModal.description', {
                        defaultValue:
                            'Adjust the device version and platform fee expiration date, and recalculate the lure expiration date from system settings after replacing the lure.',
                    })}
                </Typography.Paragraph>
                <div style={{ marginBottom: 16 }}>
                    <Typography.Text type="secondary">
                        {t('admin:package.editDeviceModal.fields.deviceName', {
                            defaultValue: 'Device Name',
                        })}
                    </Typography.Text>
                    <Typography.Text style={{ marginLeft: 8 }}>
                        {editingDevice?.deviceName}
                    </Typography.Text>
                </div>
                <Form form={editDeviceForm} layout="vertical">
                    <Form.Item
                        name="deviceVersion"
                        label={t(
                            'admin:package.editDeviceModal.fields.deviceVersion',
                            {
                                defaultValue: 'Device Version',
                            }
                        )}
                        rules={[
                            {
                                required: true,
                                message: t(
                                    'admin:package.editDeviceModal.validation.deviceVersionRequired',
                                    {
                                        defaultValue:
                                            'Please select a device version',
                                    }
                                ),
                            },
                        ]}
                    >
                        <Select
                            options={editableDeviceVersionOptions.map(
                                (option) => ({
                                    label: getDevicePackageOptionDisplayName(
                                        option,
                                        t
                                    ),
                                    value: option.value,
                                })
                            )}
                        />
                    </Form.Item>
                    <Form.Item
                        name="lureExpireDate"
                        label={t(
                            'admin:package.editDeviceModal.fields.lureExpireDate',
                            {
                                defaultValue: 'Lure Expiration Date',
                            }
                        )}
                        style={{ marginBottom: 8 }}
                        getValueProps={(value?: Dayjs | null) => ({
                            value: formatPackageFormDate(value),
                        })}
                        rules={[
                            {
                                required: true,
                                message: t(
                                    'admin:package.editDeviceModal.validation.lureExpireDateRequired',
                                    {
                                        defaultValue:
                                            'Please select a lure expiration date',
                                    }
                                ),
                            },
                        ]}
                    >
                        <Input
                            readOnly
                            aria-label={t(
                                'admin:package.editDeviceModal.fields.lureExpireDate',
                                {
                                    defaultValue: 'Lure Expiration Date',
                                }
                            )}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <div
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexWrap: 'nowrap',
                            gap: 8,
                            justifyContent: 'space-between',
                            marginBottom: 24,
                        }}
                    >
                        <Typography.Text
                            type="secondary"
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            {t('admin:package.editDeviceModal.lureHint', {
                                days: lureValidityDays,
                                defaultValue:
                                    'Click to recalculate {{days}} days from today',
                            })}
                        </Typography.Text>
                        <Button onClick={renewLureExpireDate}>
                            {t(
                                'admin:package.editDeviceModal.actions.renewLure',
                                {
                                    defaultValue: 'Replace Lure',
                                }
                            )}
                        </Button>
                    </div>
                    <Form.Item
                        name="platformExpireDate"
                        label={t(
                            'admin:package.editDeviceModal.fields.platformExpireDate',
                            {
                                defaultValue: 'Platform Fee Expiration Date',
                            }
                        )}
                        rules={[
                            {
                                required: true,
                                message: t(
                                    'admin:package.editDeviceModal.validation.platformExpireDateRequired',
                                    {
                                        defaultValue:
                                            'Please select a platform fee expiration date',
                                    }
                                ),
                            },
                        ]}
                    >
                        <ReadonlyDatePicker
                            format={DATE_FORMAT}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </PageLayout>
    )
}

export default PackageManagementV1
